import React, { ReactNode } from 'react';

import type { GlyphName } from '@neptune/shared/core-glyphs-domain';

import { bemBlock } from '../../modules/bem';
import * as layout from '../../modules/layout';
import * as uiState from '../../modules/ui-state';
import { Icon, IconProps } from '../icon/Icon';
import { LayoutColumn } from '../layout-column/LayoutColumn';
import { LayoutRow, LayoutRowProps } from '../layout-row/LayoutRow';
import { Text } from '../text/Text';

import './DropdownItem.less';

const block = bemBlock('n-DropdownItem');

export type IconDefinition = GlyphName | IconProps;
export type DropdownItemProps<P extends object> = Pick<
  LayoutRowProps<{}>,
  | 'alignItems'
  | 'alignSelf'
  | 'className'
  | 'component'
  | 'justifyContent'
  | 'onClick'
  | 'reversed'
  | 'selfSpacing'
  | 'spacedChildren'
  | 'span'
  | 'title'
  | 'width'
  | 'withGutter'
  | 'withPadding'
> &
  P & {
    children?: ReactNode;
    disabled?: boolean;
    icon?: IconDefinition | ReactNode;
    label?: ReactNode;
    description?: ReactNode;
    selected?: boolean;
    active?: boolean;
    size?: 'md' | 'lg' | 'xl' | 'grow';
    'data-role'?: string;
    submenu?: boolean;
    radius?: 'xs' | 'sm' | 'md';
  };

/** @deprecated use ActionsMenu from @neptune/shared/venus-ui instead */
export const DropdownItem = <P extends object>(props: DropdownItemProps<P>) => {
  const {
    alignItems = 'center',
    size = 'md',
    spacedChildren = 'sm',
    span = 'auto',
    withGutter = 'sm',
    withPadding = props.description && props.label ? 'lg' : undefined,
    radius = 'xs',
    active,
    children,
    className,
    disabled,
    icon,
    label,
    description,
    selected,
    submenu,
    ...passProps
  } = props;

  const clickable = !disabled;

  const ownProps: DropdownItemProps<{}> = {
    className: block({
      modifiers: {
        size,
        active,
        radius,
      },
      extra: [
        className,
        uiState.clickableClassName(clickable),
        uiState.disabledClassName(disabled),
      ],
    }),
  };

  const renderIcon = () => {
    if (!icon) {
      return null;
    }

    if (typeof icon === 'string') {
      return <Icon className={block('icon')} glyph={icon as GlyphName} fixedWidth />;
    }

    if ((icon as IconProps).glyph) {
      return <Icon className={block('icon')} {...(icon as IconProps)} fixedWidth />;
    }

    return icon;
  };

  const renderLabel = () => {
    if (label) {
      const title = typeof label === 'string' ? label : undefined;
      return (
        <Text className={layout.spanClassName('greedy')} ellipsis title={title}>
          {label}
        </Text>
      );
    }
  };

  const renderDescription = () => {
    if (!description) {
      return null;
    }

    return (
      <Text
        size="xs"
        color="text-subdued"
        fontWeight="normal"
        className={layout.spanClassName('greedy')}
        ellipsis
      >
        {description}
      </Text>
    );
  };

  const renderStateIcon = () => {
    if (submenu) {
      return <Icon glyph="chevron-right" size="xs" color="text-subdued" />;
    }

    if (selected) {
      return <Icon className={block('checkmark')} glyph="checkmark" fixedWidth />;
    }

    return null;
  };

  return (
    <LayoutRow
      data-role="menu-item"
      data-selected={selected ? 'true' : 'false'}
      data-active={active ? 'true' : 'false'}
      alignItems={alignItems}
      spacedChildren={spacedChildren}
      span={span}
      withGutter={withGutter}
      withPadding={withPadding}
      {...ownProps}
      {...passProps}
    >
      {renderIcon()}
      {(props.label || props.description) && (
        <LayoutColumn span="greedy" overflow="hidden">
          {renderLabel()}
          {renderDescription()}
        </LayoutColumn>
      )}
      {children}
      {renderStateIcon()}
    </LayoutRow>
  );
};
